import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { TYPE_EVENT_OTHER } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { checkAndProcessBeatData, countBeatChannels } from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import type { EditMoreInfoProps } from './editMoreInfo';

/**
 * Backs the extended strip editor where a technician re-captures and annotates an ECG strip. Loads a five-minute ECG and beat window around the strip start, builds the viewer object with its AI beat overlay, mirrors the incoming strip data into local state, formats captured strip images and their drawings on capture, and exposes the save, cancel, field and checkbox handlers.
 *
 * @param props - Editor props supplying the strip data, original data, ECG data map, viewer config, timezone offset, the removable flag and the save and cancel callbacks.
 * @returns An object carrying couldRemoveStrip, state, setState, ecgObject, startStripEpoch, the save, cancel, change and checkbox handlers, handleCaptureImages and getCaptureMode.
 */
const useEditMoreInfo = ({
  couldRemoveStrip = true,
  data,
  originalData,
  handleSaveEditMoreInfo,
  handleCancelEditMoreInfo,
  ecgViewerConfig,
  ecgDataMap,
  timezoneOffset,
}: Pick<
  EditMoreInfoProps,
  | 'couldRemoveStrip'
  | 'data'
  | 'originalData'
  | 'handleSaveEditMoreInfo'
  | 'handleCancelEditMoreInfo'
  | 'ecgViewerConfig'
  | 'ecgDataMap'
  | 'timezoneOffset'
>) => {
  const [state, setState] = useMergeState<any>({
    isLoading: false,
    isIncludedToReport: false,
    selected: false,
    technicianComments: '',
    title: '',
    stripImgs: [],
    draw: [],
    isRemoved: false,
    stripStart: undefined,
    aiChannel: undefined,
    isOpenRemoveStripModal: false,
  });

  const [ecgObject, setEcgObject] = useState<any>({});

  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);

  let isActiveQuery: boolean;

  /**
   * Hands the edited strip back to the parent dialog. Collects the captured images, drawings, technician comments, title, removal, include-in-report and selected flags into one payload and passes it to handleSaveEditMoreInfo.
   *
   * @returns Nothing.
   */
  const onClickSave = () => {
    const transferData = {
      stripImgs: state.stripImgs,
      draw: state.draw,
      technicianComments: state.technicianComments,
      title: state.title,
      isRemoved: state.isRemoved,
      isIncludedToReport: state.isIncludedToReport,
      selected: state.selected,
    };
    handleSaveEditMoreInfo(transferData);
  };

  /**
   * Leaves the more-info editor without saving. Calls the handleCancelEditMoreInfo callback from props, which clears the pane flag and the transfer payload.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    handleCancelEditMoreInfo();
  };

  /**
   * Records a field edit in the more-info strip form. Merges the single named key into local state.
   *
   * @param name - State key being written.
   * @param value - New value for that key.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Flips a boolean option on the more-info strip form. Inverts the current value stored under the given key.
   *
   * @param name - State key of the boolean being toggled.
   * @returns Nothing.
   */
  const onClickCheckbox = (name: any) => {
    setState({ [name]: !state[name] });
  };

  /**
   * Assembles the ECG viewer configuration for the strip being edited, including its AI beat overlay. Converts the stored drawing widgets into display coordinates against the strip start, then sets the viewer object with the sample buffer, gain, channel count, AI beat prediction layer, capture jump offset and the high-pass, notch, speed and amplitude settings taken from the viewer config or its defaults.
   *
   * @param ecg - Raw ECG sample buffer for the strip window.
   * @param beats - Beat markers rendered as the AI prediction layer.
   * @param hesBeatStatus - Per-beat HES status flags, whose length also sizes the event status array.
   * @returns Nothing.
   */
  const createObjectEcg = (ecg: any, beats: any, hesBeatStatus: any) => {
    const { gain: configGain, hpFilter, lpFilter } = ecgViewerConfig || {};
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    let drawStartSample = 0;
    const { draw, startSample } = formatDrawDisplay(
      state.draw,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    if (startSample) {
      drawStartSample = startSample;
    }
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    setEcgObject({
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: undefined,
      aiPredict: [
        {
          beats,
          hesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(hesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: drawStartSample,
      hiddenChannels: [0, 1, 2],
      hpFilters: hpFilter ?? ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: lpFilter ?? ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: 25,
      amp: configGain ?? ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: 10,
    });
  };

  /**
   * Fetches the ECG samples and beats for a 300-second window centred on the strip start. Floors the start to a whole sample, requests ECG and beat data in parallel, and only while the effect is still active records the window epochs, counts the beat channels and builds the viewer object; a cancellation is swallowed, any other failure is logged and clears the loading flag.
   *
   * @returns A promise resolving once the viewer has been populated or the failure has been handled.
   */
  const downloadDataEcg = async () => {
    setState({ isLoading: true });
    try {
      let convertStripStart = moment(state.stripStart);
      const stripStartMillisecond = convertStripStart.milliseconds();
      const dividedNumber = 1000 / ecgDataMap.samplingFrequency;
      const floorNumber = Math.floor(stripStartMillisecond / dividedNumber) * dividedNumber;
      convertStripStart = convertStripStart.milliseconds(floorNumber);
      const startStrip = convertStripStart.clone().subtract(150, 'seconds');
      const stopStrip = convertStripStart.clone().add(150, 'seconds');
      const momentObject = {
        startMoment: startStrip,
        stopMoment: stopStrip,
      };
      const dataLength =
        ((stopStrip.valueOf() - startStrip.valueOf()) / 1000) * ecgDataMap.samplingFrequency;
      const [ecgData, holterBeats] = (await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength,
          momentObject,
        } as any),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject,
        } as any),
      ])) as any;
      if (isActiveQuery) {
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        const channel = countBeatChannels(holterBeats?.beatChannels);
        createObjectEcg(ecgData, holterBeats?.beats || [], holterBeats?.hesBeatStatus || []);
        setState({
          aiChannel: channel,
          isLoading: false,
        });
      }
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setState({ isLoading: false });
    }
  };

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: any) => {
    try {
      if (allIndexStripSvg.length > 0) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        if (captureMode === CAPTURE_MODE.EVENT) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
          });
        } else if (captureMode === CAPTURE_MODE.OTHER) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
          });
        }
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Decides whether the strip is captured as an event strip or as a free-form other strip. Returns the event capture mode unless the original data carries the other-event type.
   *
   * @returns The capture mode constant for the strip being edited.
   */
  const getCaptureMode = () => {
    if (originalData?.type !== TYPE_EVENT_OTHER) {
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  useEffect(() => {
    console.log('[editMoreInfo]-data', data, originalData);
    if (data) {
      const { stripStart } = originalData;
      const {
        stripImgs,
        draw,
        technicianComments,
        title,
        isRemoved,
        isIncludedToReport,
        selected,
      } = data;
      const setStateObject = {
        stripImgs,
        draw,
        technicianComments,
        title,
        isRemoved,
        isIncludedToReport,
        selected,
        stripStart,
      };
      setState(setStateObject);
    }
  }, [data, originalData]);

  useEffect(() => {
    isActiveQuery = true;
    if (state.stripStart) {
      downloadDataEcg();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [state.stripStart]);

  useEffect(() => {
    if (!state.isIncludedToReport) {
      setState({ selected: false });
    }
  }, [state.isIncludedToReport]);

  return {
    couldRemoveStrip,
    state,
    setState,
    ecgObject,
    startStripEpoch,
    onClickSave,
    onClickCancel,
    onChange,
    onClickCheckbox,
    handleCaptureImages,
    getCaptureMode,
  };
};

export default useEditMoreInfo;
